import { computed } from '@preact/signals';
import toast from 'react-hot-toast';
import { loadNewEpisode, play } from '../global-state/audio-player';
import { currentEpisode, currentPlaylist } from '../global-state/audio-signals';
import { useUserDataStore } from '../global-state/user-data';
import { Episode } from '../models/podcasts';
import { checkIfPlayed } from '../utils/check-if-played';
import { isPlaylistSupported } from '../utils/platform';
import { trackPlayNextClick } from '../utils/plausible-events';
import { Icon } from './icon';

const currentEpisodeIdSignal = computed(() => currentEpisode.value?.id);

export function PlayNextButton({ episode }: { episode: Episode }) {
  if (!isPlaylistSupported) return null;

  const playlist = currentPlaylist.value;
  const currentEpisodeId = currentEpisodeIdSignal.value;

  return !playlist.some(([id]) => id === episode.id) ? (
    <button
      onClick={() => {
        trackPlayNextClick(episode.id);

        const progress = useUserDataStore.getState().episodesProgress[episode.id];
        const [secondsPlayed = 0] = progress || [];
        const isPlayed =
          progress && progress.length ? checkIfPlayed(episode, progress) : false;
        const startingPosition = isPlayed ? 0 : secondsPlayed;

        // If no episode is currently playing, play this episode
        if (!currentEpisodeId) {
          loadNewEpisode(episode, startingPosition);
          play();
          return;
        }

        // This episode is already playing -> do nothing
        if (currentEpisodeId === episode.id) {
          toast.success('Episode already playing');
          return;
        }

        // Add to playlist so it plays next
        currentPlaylist.value = [[episode.id, startingPosition], ...playlist];
        const count = playlist.length + 1;
        toast.success(
          count === 1 ? (
            'Playing next'
          ) : (
            <div class="flex flex-col">
              <strong class="mb-0.5">Playing next</strong>
              {count > 1 && <p>{count} episodes in queue</p>}
            </div>
          )
        );
      }}
      title="Play next"
    >
      <Icon sprite="queue-top" class="-mt-[2px] mr-1 h-[28px] w-[28px] text-gray-400" />
    </button>
  ) : (
    <button
      onClick={() => {
        currentPlaylist.value = playlist.filter(([id]) => id !== episode.id);
      }}
      title="Remove from queue"
    >
      <Icon
        sprite="unqueue-top"
        class="-mt-[2px] mr-1 h-[28px] w-[28px] text-icon-blue"
      />
    </button>
  );
}
